import {
  ExpedienteFactory,
  Expediente,
  ExpedienteFactoryError,
  ReferenciaExpediente,
  ExpedienteState,
  Award,
  Execution,
  HeavyTickets,
  Recognition,
  Accounting,
  AccountingAward,
  FinalSettlement,
  FinalSettlementPositive,
  DefinitiveWarrantyRefund,
  FinalSettlementNegative,
  HeavyTicket,
  Payment,
  FirstPayment,
  SecondPayment
} from '@/Domain/Expediente'
import { AxiosUserExpedienteData } from './AxiosUserExpedienteData'
import { NewDate, NewHour } from '@/Domain/News'
import { AxiosAwardData } from './AxiosAwardData'
import { Currency } from '@/Domain/Batch/Currency'
import { Percentage } from '@/Domain/Expediente/Percentage'
import { AxiosExecutionData } from './AxiosExecutionData'
import { AxiosRecognitionData } from './AxiosRecognitionData'
import { AxiosAcountingData } from './AxiosAcountingData'
import { AxiosTicketData } from './AxiosTicketData'
import { OfferTarget } from '@/Domain/Offer'
import { DanosEvitables } from '@/Domain/Expediente/DanosEvitables'
import { DanosMadeirables } from '@/Domain/Expediente/DanosMadeirables'
import { DanosNonMadeirables } from '@/Domain/Expediente/DanosNonMadeirables'

export class AxiosUserExpedienteFactory implements ExpedienteFactory<AxiosUserExpedienteData> {
  makeExpediente (data: AxiosUserExpedienteData): Expediente {
    this.validateData(data)
    const award = data.award ? this.buildAwardData(data.award) : null
    const execution = data.execution ? this.buildExecutionData(data.execution) : null
    const recognition = data.recognition ? this.buildRecognitionData(data.recognition) : null
    const acounting = data.acounting ? this.buildAcountingData(data.acounting) : null
    try {
      return new Expediente(
        new ReferenciaExpediente(data.numExpediente),
        data.lote || '',
        data.pertenza || '',
        data.monte || '',
        data.provincia || '',
        data.distrito || '',
        data.concello || '',
        this.parseDate(data.dataLicenciaCorta),
        this.parseDate(data.dataPrevistaRemateCorta),
        data.estado as ExpedienteState,
        award,
        execution,
        recognition,
        acounting,
        this.parseCurrencyNumber(data.importeOferta)
      )
    } catch (e) {
      throw new ExpedienteFactoryError('ExpedienteFactoryError: ' + e.message)
    }
  }

  private buildAwardData (data: AxiosAwardData): Award {
    return new Award(
      data.nomeAdxudicatario,
      new Currency(data.importeOferta),
      data.fraccionaPago === 'S',
      new Percentage(parseInt(data.iva, 10)),
      this.parseDate(data.dataNotificacion),
      this.parseDate(data.prazoDocumentacion),
      this.parseCurrency(data.gastosAnuncio),
      this.parsePercentage(data.garantiaDefinitivaPorcentaje),
      this.parseCurrencyNumber(data.garantiaDefinitivaImporte),
      this.parsePercentage(data.garantiaComplementariaPorcentaje),
      this.parseCurrencyNumber(data.garantiaComplementariaImporte),
      this.parsePercentageNumber(data.garantiaTotalPorcentaje),
      this.parseCurrencyNumber(data.garantiaTotalImporte),
      this.parseDate(data.dataIngresoMadeira),
      this.parseDate(data.dataIngresoAnuncio),
      this.parseDate(data.dataIngresoTaxas),
      this.parseDate(data.dataGarantiaTotal),
      this.parseDate(data.dataPagoIvaComunidade),
      this.parseDate(data.dataDevolucionGarantia),
      this.parseDate(data.dataNotificacionPertenza),
      this.parseDate(data.dataAdxudicacion)
    )
  }

  private buildExecutionData (data: AxiosExecutionData): Execution {
    const tickets = this.buildTickets(data.tickets)
    return new Execution(
      data.modoAlleamento,
      this.parseDate(data.dataLicenciaCorta),
      this.parseDate(data.dataActaEntrega),
      data.prazoDefinitivo ? parseInt(data.prazoDefinitivo, 10) : 0,
      this.parseDate(data.dataPrevistaRemate),
      data.solicitudeProrroga === 'S',
      parseInt(data.prazoSolicitude),
      this.parseDate(data.dataLimiteCorta),
      data.resolucion,
      this.parseDate(data.dataResolucion),
      this.parseDate(data.dataAmpliacionPrazo),
      data.observaciones ? data.observaciones : '',
      data.balance,
      new HeavyTickets(
        tickets,
        this.parseCurrencyNumber(data.totalTickets)
      )
    )
  }

  private buildTickets (data: AxiosTicketData[]): HeavyTicket[] {
    const tickets: HeavyTicket[] = []
    for (const ticket of data) {
      const target = this.buildDestinoMadeira(ticket.destinoMadeira)
      const item = new HeavyTicket(
        ticket.especie,
        parseInt(ticket.forma, 10),
        target,
        ticket.numMatricula,
        this.parseDate(ticket.dataPesada),
        new Currency(ticket.peso),
        new Currency(ticket.custoTotalTicket)
      )
      tickets.push(item)
    }
    return tickets
  }

  private buildDestinoMadeira (target: string): OfferTarget {
    if (target.indexOf('Serra 2') !== -1) {
      return OfferTarget.SERRA2
    }
    if (target.indexOf('Serra 1') !== -1) {
      return OfferTarget.SERRA1
    }
    return OfferTarget.TRITURACION
  }

  private buildRecognitionData (data: AxiosRecognitionData): Recognition {
    const danosEvitables = new DanosEvitables(
      data.descricionDanos,
      this.parseCurrencyNumber(data.importeDanosEvitables),
      this.parsePercentage(data.importeIveDanosEvitables),
      this.parseDate(data.dataRemision)
    )

    const danosNonMadeirables = new DanosNonMadeirables(
      this.parseCurrencyNumber(data.importeDanosNonMadeirables),
      this.parsePercentage(data.iveDanosNonMadeirable),
      this.parseCurrencyNumber(data.importeIveDanosNonMadeirable),
      this.parseCurrencyNumber(data.importeTotalDanosNonMadeirable),
      this.parseDate(data.dataNotificacionAdxNonMadeirables),
      data.reparacionDanos === 'S',
      this.parseDate(data.dataPagamentoDanosNonMadeirables)
    )

    const danosMadeirables = new DanosMadeirables(
      this.parseCurrencyNumber(data.importeDanosMadeirables),
      this.parsePercentage(data.iveDanosMadeirable),
      this.parseCurrencyNumber(data.importeIveDanosMadeirable),
      this.parseCurrencyNumber(data.importeTotalDanosMadeirable),
      this.parseDate(data.dataNotificacionAdxMadeirables),
      this.parseDate(data.dataPagamentoDanosMadeirables)
    )
    const hour = data.horaActa ? new NewHour(data.horaActa) : null
    const recognition = new Recognition(
      this.parseDate(data.dataActa),
      hour,
      data.numSancionador,
      data.observacions ? data.observacions : '',
      data.danos === 'S',
      data.tipoDanos,
      data.afeccionDanos,
      danosEvitables,
      danosMadeirables,
      danosNonMadeirables
    )
    return recognition
  }

  private buildAcountingData (data: AxiosAcountingData): Accounting {
    const accountingAward = new AccountingAward(
      this.parseCurrencyNumber(data.accountingAwardImporteAdxudicacion),
      this.parsePercentage(data.accountingAwardIva),
      this.parseCurrencyNumber(data.accountingAwardImporteIvaAdxudicacion),
      this.parseDate(data.accountingAwardDataIngresoAdxudicatario),
      this.parseDate(data.accountingAwardDataIngresoIvaPropetario),
      this.parsePercentage(data.accountingAwardParticipacionPropiedad),
      this.parseCurrencyNumber(data.accountingAwardImportePropiedade),
      this.parseDate(data.accountingAwardDataPagoPropiedade),
      this.parsePercentageNumber(data.accountingAwardParticipacionAdministracion),
      this.parseCurrencyNumber(data.accountingAwardImporteDevolucionAdministracion),
      this.parseDate(data.accountingAwardDataPagoAdministracion),
      data.codModoAlleamentoDef
    )
    const finalSettlement = new FinalSettlement(
      this.parseCurrencyNumber(data.finalSettlementImporteAdxudicacion),
      this.parseCurrencyNumber(data.finalSettlementImporteAdxudacionIva),
      this.parseCurrencyNumber(data.finalSettlementImporteMadeiraExtraida),
      this.parseCurrencyNumber(data.finalSettlementimporteMadeiraExtraidaIva),
      data.finalSettlementbalance
    )
    const finalSettlementPositive = new FinalSettlementPositive(
      this.parseCurrencyNumber(data.positiveFinalSettlementimporteLiquidar),
      this.parsePercentage(data.positiveFinalSettlementIva),
      this.parseCurrencyNumber(data.positiveFinalSettlementImporteLiquidarIva),
      this.parseDate(data.positiveFinalSettlementDataIngresoAdxudicatario),
      this.parseDate(data.positiveFinalSettlementDataIngresoIvaPropetario),
      this.parsePercentage(data.positiveFinalSettlementParticipacionPropiedad),
      this.parseCurrencyNumber(data.positiveFinalSettlementImportePropiedade),
      this.parseDate(data.positiveFinalSettlementDataPagoPropiedade),
      this.parsePercentageNumber(data.positiveFinalSettlementParticipacionAdministracion),
      this.parseCurrencyNumber(data.positiveFinalSettlementImporteDevolucionAdministracion),
      this.parseDate(data.positiveFinalSettlementDataPagoAdministracion)
    )
    const finalSettlementNegative = new FinalSettlementNegative(
      this.parseCurrencyNumber(data.negativeFinalSettlementImporteIngresarMadeira),
      this.parseCurrencyNumber(data.negativeFinalSettlementImporteIngresarIva),
      this.parseCurrencyNumber(data.negativeFinalSettlementImporteIngresarTotal),
      this.parseDate(data.negativeFinalSettlementDataingreso)
    )
    const definitiveWarrantyRefund = new DefinitiveWarrantyRefund(
      data.definitiveWarrantyRefundTipoDevolucion,
      this.parseCurrencyNumber(data.definitiveWarrantyRefundImporteDevolucion),
      this.parseDate(data.definitiveWarrantyRefundDataDevolucion),
      data.definitiveWarrantyPartialRefoundCause,
      this.parseDate(data.definitiveWarrantyPartialIncautacionDate),
      this.parseCurrencyNumber(data.definitiveWarrantyPartialIncautacionImport),
      this.parseDate(data.definitiveWarrantyPartialRefoundToProperty),
      data.definitiveWarrantyContrinximentoCause,
      this.parseDate(data.definitiveWarrantyConstriximentoResolutionDate),
      this.parseCurrencyNumber(data.definitiveWarrantyConstriximentoImport),
      this.parseDate(data.definitiveWarrantyConstriximentoRemisionDate)
    )

    const payment = new Payment(
      this.parseCurrencyNumber(data.accountingAwardImporteDanos),
      this.parseCurrencyNumber(data.accountingAwardIveDanos),
      this.parseDate(data.accountingAwardDataIngresoAdxDanos),
      this.parseDate(data.accountingAwardDataIngresoIvePropDanos),
      this.parsePercentage(data.accountingAwardParticipacionPropDanos),
      this.parseCurrencyNumber(data.accountingAwardImportePropiedadeDanos),
      this.parseDate(data.accountingAwardDataPagoPropiedadeDanos),
      this.parsePercentage(data.accountingAwardParticipacionAdminDanos),
      this.parseCurrencyNumber(data.accountingAwardImporteAdminDanos),
      this.parseDate(data.accountingAwardDataPagoAdminDanos)
    )

    const firstPayment = new FirstPayment(
      this.parseCurrencyNumber(data.accountingAwardImportePrimerPago),
      this.parseCurrencyNumber(data.accountingAwardIvePrimerPago),
      this.parseCurrencyNumber(data.accountingAwardImporteAdxudacionIvePrimerPago),
      this.parseDate(data.accountingAwardDataIngresoAdxPrimerPago),
      this.parseDate(data.accountingAwardDataIngresoIvePropPrimerPago),
      this.parsePercentage(data.accountingAwardParticipacionPropPrimerPago),
      this.parseCurrencyNumber(data.accountingAwardImportePropiedadePrimerPago),
      this.parseDate(data.accountingAwardDataPagoPropiedadePrimerPago),
      this.parsePercentage(data.accountingAwardParticipacionAdminPrimerPago),
      this.parseCurrencyNumber(data.accountingAwardImporteAdminPrimerPago),
      this.parseDate(data.accountingAwardDataPagoAdminPrimerPago)
    )

    const secondPayment = new SecondPayment(
      this.parseCurrencyNumber(data.accountingAwardImporteSegundoPago),
      this.parseDate(data.accountingAwardDataIngresoAdxSegundoPago),
      this.parsePercentage(data.accountingAwardParticipacionPropSegundoPago),
      this.parseCurrencyNumber(data.accountingAwardImportePropiedadeSegundoPago),
      this.parseDate(data.accountingAwardDataPagoPropiedadeSegundoPago),
      this.parsePercentage(data.accountingAwardParticipacionAdminSegundoPago),
      this.parseCurrencyNumber(data.accountingAwardImporteAdminSegundoPago),
      this.parseDate(data.accountingAwardDataPagoAdminSegundoPago)
    )
    return new Accounting(
      accountingAward,
      finalSettlement,
      finalSettlementPositive,
      finalSettlementNegative,
      definitiveWarrantyRefund,
      payment,
      firstPayment,
      secondPayment,
      data.fraccionaPago === 'S',
      data.danos === 'S'
    )
  }

  private validateData (data: AxiosUserExpedienteData): void {
    const requiredFields = ['numExpediente', 'lote', 'pertenza', 'monte', 'provincia', 'distrito', 'concello']
    for (const field of requiredFields) {
      const parsedField = field as keyof AxiosUserExpedienteData
      if (!data[parsedField]) {
        throw new ExpedienteFactoryError('ExpedienteFactoryError: required field ' + field + ' not available')
      }
    }
  }

  private parseDate (rawDate: string | null): NewDate | null {
    if (!rawDate) {
      return null
    }
    return new NewDate(rawDate)
  }

  private parseCurrency (rawCurrency: string | null): Currency | null {
    if (!rawCurrency) {
      return null
    }
    return Currency.fromString(rawCurrency)
  }

  private parseCurrencyNumber (rawCurrency: number | null): Currency | null {
    if (!rawCurrency) {
      return null
    }
    return new Currency(rawCurrency)
  }

  private parsePercentage (rawPercentage: string | null): Percentage | null {
    if (!rawPercentage) {
      return null
    }
    return new Percentage(parseFloat(rawPercentage))
  }

  private parseBoolean (rawBoolean: string | null): boolean | null {
    if (!rawBoolean) {
      return null
    } else if (rawBoolean === 'True') {
      return true
    } else {
      return false
    }
  }

  private parsePercentageNumber (rawPercentage: number | null): Percentage | null {
    if (rawPercentage === null) {
      return null
    }
    return new Percentage(rawPercentage)
  }
}
